import React, { useEffect, useState } from "react";

import SmartRtpEditFormLoader from "./components/SmartRtpEditFormLoader";
import {
  getGamesList,
  getSettingsById,
  getSettingsList,
  saveSettings,
  getBigPrizeMonitorList,
} from "../../../controllers/V1/smartRtp/smartRtpController";

const YN_OPTIONS = [
  { label: "Yes", value: "Y" },
  { label: "No", value: "N" },
];

function normalizePos(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return /^\d{4}$/.test(digits) ? digits : null;
}

async function hideWinnerGivenConfigs(row) {
  if (!row) return row;
  const gameId =
    row.game_id && typeof row.game_id === "object"
      ? row.game_id._id || row.game_id
      : row.game_id;
  const monitor = await getBigPrizeMonitorList({
    ...(row._id ? { setting_id: String(row._id) } : {}),
    ...(gameId ? { game_id: String(gameId) } : {}),
  });
  const givenKeys = new Set();
  for (const item of monitor?.result || []) {
    if (item.winner_status !== "Winner Given" && item.winner_status !== "Award Reserved") {
      continue;
    }
    const configId = String(item.config_id || "").trim();
    const pos = normalizePos(item.pos_number);
    if (configId && pos) givenKeys.add(`${configId}:${pos}`);
  }

  const slots = Array.isArray(row.big_prize_time_slots)
    ? row.big_prize_time_slots
        .map((slot) => {
          const configId = String(slot.config_id || "").trim();
          const posList = (slot.pos_numbers || slot.mobiles || [])
            .map(normalizePos)
            .filter(Boolean);
          const remainingPos = posList.filter(
            (pos) => !givenKeys.has(`${configId}:${pos}`)
          );
          if (!remainingPos.length) return null;
          return remainingPos.length === posList.length
            ? slot
            : { ...slot, pos_numbers: remainingPos };
        })
        .filter(Boolean)
    : [];

  return { ...row, big_prize_time_slots: slots };
}

export default function SmartRtpSettingsFormPage() {
  const [gameOptions, setGameOptions] = useState([]);

  useEffect(() => {
    const loadGames = async () => {
      const res = await getGamesList({ type: "", condition: {}, limit: 0, skip: 0, sort: { title: 1 } });
      if (res.status === true) {
        setGameOptions(
          (res.result || []).map((item) => ({ label: item.title, value: item._id }))
        );
      }
    };
    loadGames();
  }, []);

  const resolveByGameId = async (gameId) => {
    const res = await getSettingsList({
      type: "",
      condition: { game_id: gameId, setting_type: "Game" },
      limit: 1,
      skip: 0,
      sort: { createdAt: -1 },
    });
    if (res?.status === true && Array.isArray(res.result) && res.result.length > 0) {
      return hideWinnerGivenConfigs(res.result[0]);
    }
    return null;
  };

  const fields = [
    {
      name: "setting_type",
      label: "Setting Type",
      type: "select",
      options: [
        { label: "Global", value: "Global" },
        { label: "Game", value: "Game" },
      ],
      rules: [{ required: true, message: "Setting type is required." }],
    },
    {
      name: "game_id",
      label: "Game",
      type: "select",
      options: gameOptions,
    },
    {
      name: "enable_smart_rtp",
      label: "Enable Smart RTP",
      type: "select",
      options: YN_OPTIONS,
    },
    {
      name: "enable_buyer_reward_engine",
      label: "Enable BRE",
      type: "select",
      options: YN_OPTIONS,
    },
    {
      name: "enable_consecutive_loss",
      label: "Enable Consecutive Loss Compassion",
      type: "select",
      options: YN_OPTIONS,
    },
    {
      name: "consecutive_loss_threshold",
      label: "Loss Threshold (consecutive losing orders on this game)",
      type: "number",
      props: { min: 1, max: 50 },
      rules: [{ required: true, message: "Loss threshold is required." }],
    },
    {
      name: "compassion_window_orders",
      label: "Win Window (a winner is forced within these orders)",
      type: "number",
      props: { min: 1, max: 20 },
      rules: [{ required: true, message: "Compassion window is required." }],
    },
    {
      name: "compassion_restrict_to_consolation",
      label: "Compassion: Consolation Slabs Only",
      type: "select",
      options: YN_OPTIONS,
    },
    {
      name: "max_compassion_boosts_per_day",
      label: "Max Compassion Wins / Buyer / Day",
      type: "number",
      props: { min: 0, max: 200 },
    },
    {
      name: "win_cooldown_orders",
      label: "Win Cooldown Orders",
      type: "number",
      props: { min: 0 },
    },
    { name: "global_rtp_target", label: "Prize RTP Target %", type: "number" },
    { name: "global_rtp_min", label: "Prize RTP Min %", type: "number" },
    { name: "global_rtp_max", label: "Prize RTP Max %", type: "number" },
    { name: "big_prize_threshold", label: "Big Prize Threshold", type: "number" },
    {
      name: "freeze_mode",
      label: "Freeze Mode",
      type: "select",
      options: [
        { label: "Enable", value: "Enable" },
        { label: "Disable", value: "Disable" },
      ],
    },
    { name: "freeze_start_time", label: "Freeze Start Time", props: { type: "time" } },
    { name: "freeze_end_time", label: "Freeze End Time", props: { type: "time" } },
    { name: "engine_policy_version", label: "Engine Policy Version" },
    {
      name: "shadow_mode",
      label: "Shadow Mode",
      type: "select",
      options: YN_OPTIONS,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "A" },
        { label: "Inactive", value: "I" },
        { label: "Deleted", value: "D" },
      ],
    },
    {
      name: "unmatched_random_prize_numbers",
      label: "Unmatched Random Prize Number",
      type: "numberList",
      gameOnly: true,
      itemLabel: "Random Prize Number",
      help: "Display only on non-match board slots. Never used for wins or payout.",
    },
    {
      name: "big_prize_time_slots",
      label: "Big Prize & Time Slot Configuration",
      type: "bigPrizeTimeSlots",
      gameOnly: true,
    },
  ];

  return (
    <SmartRtpEditFormLoader
      titlePrefix="Smart RTP Settings"
      backTo="/smart-rtp/games"
      fields={fields}
      fetchById={async (editId) => {
        const res = await getSettingsById(editId);
        if (res?.status === true && res.result) {
          return { ...res, result: await hideWinnerGivenConfigs(res.result) };
        }
        return res;
      }}
      resolveByGameId={resolveByGameId}
      showGameSummary
      defaultsForGameId={(gameId) => ({
        setting_type: "Game",
        game_id: gameId,
      })}
      mapInitial={(row) => ({
        ...row,
        unmatched_random_prize_numbers: Array.isArray(row?.unmatched_random_prize_numbers)
          ? row.unmatched_random_prize_numbers.map(Number)
          : [],
        big_prize_time_slots: Array.isArray(row?.big_prize_time_slots)
          ? row.big_prize_time_slots.map((slot) => ({
              ...slot,
              config_id: slot.config_id || `bps_${slot._id || Date.now()}`,
              pos_numbers: Array.isArray(slot.pos_numbers)
                ? slot.pos_numbers.map(String).filter((n) => /^\d{4}$/.test(n))
                : Array.isArray(slot.mobiles)
                  ? slot.mobiles.map(String).filter((n) => /^\d{4}$/.test(n))
                  : [],
              distribution_type: slot.distribution_type || "QuickPrize",
              anytime_order_min: Number(slot.anytime_order_min) || 1,
              anytime_order_max: Number(slot.anytime_order_max) || 5,
              prize_kind: slot.prize_kind || (slot.product_name ? "Product" : "Amount"),
              product_name: slot.product_name || "",
              start_time: slot.start_time || "00:00",
              end_time: slot.end_time || "23:59",
              valid_date: slot.valid_date || "",
              status: slot.status || "A",
            }))
          : [],
      })}
      onSave={(values, editId) => {
        const isGameSettings = values.setting_type === "Game";
        const {
          unmatched_random_prize_numbers: _unmatched,
          big_prize_time_slots: _bigPrize,
          ...rest
        } = values;
        return saveSettings({
          ...rest,
          ...(isGameSettings
            ? {
                unmatched_random_prize_numbers: Array.isArray(values.unmatched_random_prize_numbers)
                  ? values.unmatched_random_prize_numbers
                      .map((n) => Number(n))
                      .filter((n) => Number.isFinite(n) && n >= 0)
                  : [],
                big_prize_time_slots: Array.isArray(values.big_prize_time_slots)
                  ? values.big_prize_time_slots.map((slot) => ({
                      config_id: slot.config_id || `bps_${Date.now()}`,
                      distribution_type: slot.distribution_type || "QuickPrize",
                      anytime_order_min: Math.max(
                        1,
                        Math.floor(Number(slot.anytime_order_min) || 1)
                      ),
                      anytime_order_max: Math.max(
                        Math.max(1, Math.floor(Number(slot.anytime_order_min) || 1)),
                        Math.floor(Number(slot.anytime_order_max) || 5)
                      ),
                      prize_kind: slot.prize_kind || "Amount",
                      product_name:
                        slot.prize_kind === "Amount"
                          ? ""
                          : String(slot.product_name || "").trim(),
                      big_prize_amount:
                        slot.prize_kind === "Product"
                          ? 0
                          : Number(slot.big_prize_amount) || 0,
                      start_time: slot.start_time || "00:00",
                      end_time: slot.end_time || "23:59",
                      valid_date: slot.valid_date || undefined,
                      pos_numbers: Array.isArray(slot.pos_numbers)
                        ? slot.pos_numbers
                            .map(String)
                            .map((m) => m.replace(/\D/g, ""))
                            .filter((n) => /^\d{4}$/.test(n))
                        : [],
                      status: slot.status || "A",
                    }))
                  : [],
              }
            : {}),
          ...(editId ? { editId } : {}),
        });
      }}
      defaultValues={{
        setting_type: "Global",
        enable_smart_rtp: "Y",
        enable_buyer_reward_engine: "Y",
        enable_consecutive_loss: "Y",
        consecutive_loss_threshold: 3,
        compassion_window_orders: 3,
        compassion_restrict_to_consolation: "Y",
        max_compassion_boosts_per_day: 50,
        win_cooldown_orders: 0,
        global_rtp_target: 70,
        global_rtp_min: 50,
        global_rtp_max: 90,
        freeze_mode: "Disable",
        shadow_mode: "N",
        status: "A",
        unmatched_random_prize_numbers: [],
        big_prize_time_slots: [],
      }}
    />
  );
}
